import type { BookableCandidateDto } from "@repo/api";
import { createContext } from "react";
import type { ParsedAvailability } from "@/features/scheduling/lib/schedule/availability";
import type {
  Assignment,
  RailAppointment,
  RailBlock,
  RailDeviceBand,
  RailSelection,
  ScheduleMode,
  SessionBookingTarget,
} from "./types";

/**
 * Generic context interface: state + actions (dependency-injection-friendly).
 * `ScheduleSessionsProvider` is the only place that knows how the scheduling
 * state machine is wired (slot fetching, assignment, sequential booking); the
 * header / sidebar / step / footer pieces are oblivious and just consume this.
 */
export interface ScheduleSessionsState {
  open: boolean;
  sessions: SessionBookingTarget[];
  activeIdx: number;
  activeSession: SessionBookingTarget | undefined;
  /** orderSetId → chosen slot. */
  assignments: Record<string, Assignment>;
  assignedCount: number;
  allAssigned: boolean;
  selectedDate: Date | undefined;
  /**
   * The candidate placed (but not yet confirmed) for the active session —
   * place-then-confirm. `null` until the user picks a slot on the active date.
   * Cleared on date/room change and on advance to the next session.
   */
  placedCandidate: BookableCandidateDto | null;
  mode: ScheduleMode;
  busy: boolean;
  /** Brief dim while auto-advancing to the next unassigned session. */
  advancing: boolean;
  // ── Slot availability (active session + selected date, filtered by room) ──
  candidates: BookableCandidateDto[];
  isLoadingSlots: boolean;
  slotsError: string | null;
  // ── Room (location) filter ──
  /** Distinct rooms offered across the active date's candidates. Empty when the
   *  catalog carries no location requirement (selector then hidden). */
  rooms: { id: string; name: string }[];
  /** Chosen room id, or `null` for "any room" (no filter). */
  selectedRoom: string | null;
  // ── Care manager / schedule ──
  hasSchedule: boolean;
  isLoadingSchedule: boolean;
  /** Care-manager working hours (day-of-week + start/end) for the rail's window
   *  and day-off detection. `null` when the schedule has no availability set. */
  dayAvailability: ParsedAvailability | null;
  /** Blocked / unavailable intervals on the care-manager schedule for the selected
   *  date — the rail hatches these as "off" context (labelled when a reason exists). */
  busyBands: RailBlock[];
  /** Existing booked appointments on the care-manager schedule for the selected date
   *  — the rail renders these as cards (distinct from blocked hatching). */
  appointments: RailAppointment[];
  /** Booked intervals of the session's required devices for the selected date —
   *  the rail overlays these so device conflicts are visible, not just the CM's. */
  deviceBands: RailDeviceBand[];
  /** Our own already-chosen sessions on the selected date (other than the active
   *  one) — rendered as blocks so they can't be booked over. */
  selections: RailSelection[];
  practitionerName: string;
  /** Dates already assigned a slot — dotted on the calendar. */
  assignedDates: Date[];
  /**
   * Valid time window for the active session, derived from its neighbours by
   * session index. Sessions are ordered: the active session must start at/after
   * the latest-ending earlier session and end at/before the earliest-starting
   * later session — which also makes two sessions sharing a slot impossible.
   * ISO bounds; `null` when that side is unconstrained.
   */
  bounds: { minStart: string | null; maxEnd: string | null };
}

export interface ScheduleSessionsActions {
  /** Open-change guarded against closing mid-book. */
  setOpen: (open: boolean) => void;
  switchToGroup: (idx: number) => void;
  selectDate: (date: Date | undefined) => void;
  /** Place a candidate on the active session (highlights it; does not commit). */
  placeCandidate: (candidate: BookableCandidateDto) => void;
  /** Release the active session’s proposed or locally confirmed time. */
  clearActiveTime: () => void;
  /** Filter the active date's candidates to a room, or `null` for any. */
  setRoom: (roomId: string | null) => void;
  /** Commit the placed candidate to the active session and advance to the next unassigned. */
  confirmActive: () => void;
  goToReview: () => void;
  backToScheduling: () => void;
  book: () => void;
}

export interface ScheduleSessionsContextValue {
  state: ScheduleSessionsState;
  actions: ScheduleSessionsActions;
}

export const ScheduleSessionsContext = createContext<ScheduleSessionsContextValue | null>(null);
